import { useEffect } from 'react';
import { Container, Paper, Title, Text, TextInput, PasswordInput, Button, Stack, Anchor, Divider } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IconBrandFacebook } from '@tabler/icons-react';
import { Layout } from '../../shared/components';
import { useAuth } from './contexts/AuthContext';
import type { SignInFormValues } from '../../types/auth';
import { useSEO } from '../../shared/hooks';
import { trackLoginStarted } from '../../utils/analytics';
import { saveOSDBeforeAuth } from '../../utils/auth/osdPreservation';
import type { OSDCode } from '../../utils/osdMetadata';

export function Login() {
  useSEO({ title: 'Logowanie', noindex: true });
  const navigate = useNavigate();
  const params = useParams<{ osd?: string }>();
  const currentOSD = (params.osd || 'TAURON') as OSDCode;
  const { signIn, signInWithFacebook, loading } = useAuth();

  // Track login form view
  useEffect(() => {
    trackLoginStarted('email');
  }, []);

  const form = useForm<SignInFormValues>({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Nieprawidłowy adres email'),
      password: (value) => (value.length >= 8 ? null : 'Hasło musi mieć co najmniej 8 znaków'),
    },
  });

  const handleSubmit = async (values: SignInFormValues) => {
    try {
      await signIn(values.email, values.password);

      // Show success notification
      notifications.show({
        title: 'Zalogowano pomyślnie!',
        message: 'Witamy ponownie w Taryfy Prądu',
        color: 'green',
      });

      // Navigate to dashboard with OSD prefix (navigate returns a promise but we intentionally don't await it)
      void navigate(`/${currentOSD}/konto/panel`);
    } catch (error) {
      // Error is already handled in AuthContext and shown via context error state
      // We can also show a notification for better UX
      notifications.show({
        title: 'Błąd logowania',
        message: error instanceof Error ? error.message : 'Nie udało się zalogować',
        color: 'red',
      });
    }
  };

  const handleFacebookLogin = async () => {
    try {
      trackLoginStarted('facebook');
      // Save current OSD before OAuth redirect
      saveOSDBeforeAuth(currentOSD);
      await signInWithFacebook();
      // User will be redirected to Facebook OAuth
      // On callback, they'll be logged in automatically
    } catch (error) {
      notifications.show({
        title: 'Błąd logowania przez Facebook',
        message: error instanceof Error ? error.message : 'Nie udało się zalogować',
        color: 'red',
      });
    }
  };

  return (
    <Layout isAuthenticated={false}>
      <Container size="xs" py={80}>
        <Paper withBorder shadow="md" p={30} radius="md">
          <Title order={2} ta="center" mb="md">
            Witaj ponownie!
          </Title>
          <Text c="dimmed" size="sm" ta="center" mb="xl">
            Zaloguj się do swojego konta
          </Text>

          {/* Facebook Login Button */}
          <Button
            fullWidth
            leftSection={<IconBrandFacebook size={20} />}
            variant="filled"
            color="#1877F2"
            onClick={handleFacebookLogin}
            loading={loading}
            mb="md"
          >
            Zaloguj przez Facebook
          </Button>

          <Divider label="lub" labelPosition="center" my="lg" />

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <TextInput
                label="Adres email"
                placeholder="twoj@email.pl"
                required
                {...form.getInputProps('email')}
              />

              <PasswordInput
                label="Hasło"
                placeholder="Twoje hasło"
                required
                {...form.getInputProps('password')}
              />

              <Anchor component={Link} to={`/${currentOSD}/reset-password`} size="sm">
                Zapomniałeś hasła?
              </Anchor>

              <Button type="submit" fullWidth mt="md" loading={loading}>
                Zaloguj się
              </Button>
            </Stack>
          </form>

          <Text c="dimmed" size="sm" ta="center" mt="xl">
            Nie masz konta?{' '}
            <Anchor component={Link} to={`/${currentOSD}/signup`} fw={600}>
              Załóż konto
            </Anchor>
          </Text>
        </Paper>
      </Container>
    </Layout>
  );
}
